/**
 * Google 연결 (Google Identity Services, 인가 코드 + 갱신 토큰).
 *
 * - 처음 한 번만 팝업으로 승인 → 서버 기능(/api/oauth/token)이 갱신 토큰을 받아 준다.
 * - 이후 액세스 토큰(1시간)이 만료되면 갱신 토큰으로 조용히 새로 받는다 (팝업 없음).
 * - 갱신 토큰은 잠금 해제된 동안만 메모리에 있고, 저장은 vault 키로 암호화해서 한다 (services/syncService).
 * - 앱을 잠그면 메모리의 토큰을 모두 지운다.
 */
import { DRIVE_SCOPE, GOOGLE_CLIENT_ID } from '@/config';
import { RemoteAuthError, RemoteUnavailableError } from '@/data/sync/remote';

type CodeResponse = { code?: string; error?: string; scope?: string };
type CodeClient = { requestCode: () => void };
type TokenClient = { requestAccessToken: (o?: { prompt?: string }) => void };
type Gis = {
  accounts: {
    oauth2: {
      initCodeClient: (cfg: {
        client_id: string;
        scope: string;
        ux_mode: 'popup';
        callback: (r: CodeResponse) => void;
        error_callback?: (e: { type: string }) => void;
      }) => CodeClient;
      initTokenClient: (cfg: {
        client_id: string;
        scope: string;
        callback: (r: { access_token?: string; expires_in?: number; error?: string }) => void;
        error_callback?: (e: { type: string }) => void;
      }) => TokenClient;
      revoke: (token: string, done?: () => void) => void;
    };
  };
};

type TokenReply = {
  access_token?: string;
  expires_in?: number;
  refresh_token?: string;
  scope?: string;
  error?: string;
};

let gisPromise: Promise<Gis> | null = null;
let access: { value: string; expiresAt: number } | null = null;
let refreshToken: string | null = null;
let refreshing: Promise<string> | null = null;
/** 서버 기능에 secret이 설정되어 "계속 연결"을 쓸 수 있는지 (미리 확인해 둔다: 클릭 직후 팝업이 막히지 않게) */
let brokerReady: boolean | null = null;

async function checkBroker(): Promise<boolean> {
  try {
    const r = await fetch('/api/oauth/token', { method: 'GET' });
    brokerReady = r.ok && ((await r.json()) as { configured?: boolean }).configured === true;
  } catch {
    brokerReady = false;
  }
  return brokerReady;
}

export class GoogleAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GoogleAuthError';
  }
}

function loadGis(): Promise<Gis> {
  gisPromise ??= new Promise((resolve, reject) => {
    const w = window as unknown as { google?: Gis };
    if (w.google?.accounts?.oauth2) return resolve(w.google);
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () =>
      w.google ? resolve(w.google) : reject(new GoogleAuthError('Google 로그인 도구를 불러오지 못했습니다'));
    s.onerror = () => {
      gisPromise = null;
      reject(new GoogleAuthError('Google 로그인 도구를 불러오지 못했습니다 (네트워크 확인)'));
    };
    document.head.appendChild(s);
  });
  return gisPromise;
}

/** 앱 시작 시 미리 불러두면 버튼 클릭 직후 팝업이 바로 열린다 (팝업 차단 방지) */
export function preloadGoogle(): void {
  if (!navigator.onLine) return;
  void loadGis().catch(() => undefined);
  void checkBroker();
}

async function callBroker(body: Record<string, string>): Promise<TokenReply> {
  let res: Response;
  try {
    res = await fetch('/api/oauth/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new RemoteUnavailableError();
  }
  const data = (await res.json().catch(() => ({}))) as TokenReply;
  if (res.status === 400 && (data.error === 'invalid_grant' || data.error === 'bad_request'))
    throw new RemoteAuthError();
  if (data.error === 'invalid_client')
    throw new GoogleAuthError('서버에 등록된 Google 클라이언트 보안 비밀번호가 맞지 않습니다 (Cloudflare 설정 확인 필요)');
  if (!res.ok || !data.access_token) throw new RemoteUnavailableError(`토큰 서버 오류 (${data.error ?? res.status})`);
  return data;
}

function setAccess(r: TokenReply) {
  access = { value: r.access_token!, expiresAt: Date.now() + (r.expires_in ?? 3600) * 1000 };
}

export function hasValidToken(): boolean {
  return !!access && access.expiresAt - Date.now() > 60_000;
}

/** 팝업 없이 계속 쓸 수 있는 상태인가 (갱신 토큰 보유) */
export function canStayConnected(): boolean {
  return !!refreshToken;
}

export function getRefreshToken(): string | null {
  return refreshToken;
}

export function setRefreshToken(t: string | null): void {
  refreshToken = t;
}

/**
 * 사용자 클릭 안에서 호출: 팝업으로 승인 → 갱신 토큰 확보.
 * @returns 새로 받은 갱신 토큰 (암호화 저장은 호출한 쪽이 한다). 받지 못했으면 null.
 */
export async function connectGoogle(): Promise<string | null> {
  const gis = await loadGis();
  if (brokerReady === false) {
    // 서버 기능이 아직 준비되지 않음: 1시간짜리 연결로 동작 (secret 설정 후에는 자동으로 계속 연결)
    await connectShortLived(gis);
    void checkBroker();
    return null;
  }
  const code = await new Promise<string>((resolve, reject) => {
    const client = gis.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: DRIVE_SCOPE,
      ux_mode: 'popup',
      callback: (r) => {
        if (r.error || !r.code) return reject(new GoogleAuthError('Google 연결이 취소되었거나 실패했습니다'));
        if (r.scope && !r.scope.split(' ').includes(DRIVE_SCOPE)) {
          return reject(new GoogleAuthError('Drive 앱 데이터 접근 권한을 허용해야 동기화할 수 있습니다'));
        }
        resolve(r.code);
      },
      error_callback: (e) =>
        reject(
          new GoogleAuthError(
            e.type === 'popup_failed_to_open'
              ? '팝업이 차단되었습니다. 팝업을 허용해 주세요.'
              : 'Google 연결이 취소되었습니다',
          ),
        ),
    });
    client.requestCode();
  });
  const reply = await callBroker({ grant: 'code', code });
  setAccess(reply);
  if (reply.refresh_token) refreshToken = reply.refresh_token;
  return reply.refresh_token ?? null;
}

function connectShortLived(gis: Gis): Promise<void> {
  return new Promise((resolve, reject) => {
    const client = gis.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: DRIVE_SCOPE,
      callback: (r) => {
        if (r.error || !r.access_token) return reject(new GoogleAuthError('Google 연결이 취소되었거나 실패했습니다'));
        setAccess(r);
        resolve();
      },
      error_callback: (e) =>
        reject(
          new GoogleAuthError(
            e.type === 'popup_failed_to_open'
              ? '팝업이 차단되었습니다. 팝업을 허용해 주세요.'
              : 'Google 연결이 취소되었습니다',
          ),
        ),
    });
    client.requestAccessToken({ prompt: '' });
  });
}

/** 동기화 엔진용: 만료되었으면 갱신 토큰으로 조용히 새로 받는다 */
export async function getAccessToken(): Promise<string> {
  if (access && hasValidToken()) return access.value;
  if (!refreshToken) throw new RemoteAuthError();
  refreshing ??= (async () => {
    try {
      const reply = await callBroker({ grant: 'refresh', refresh_token: refreshToken! });
      setAccess(reply);
      return reply.access_token!;
    } catch (e) {
      if (e instanceof RemoteAuthError) refreshToken = null; // 취소·만료된 갱신 토큰
      throw e;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** 잠금 시: 메모리의 토큰을 모두 지운다 (저장된 암호문은 그대로) */
export function clearTokens(): void {
  access = null;
  refreshToken = null;
}

/** 연결 해제: Google 쪽 권한도 취소 */
export function disconnectGoogle(): void {
  const t = refreshToken ?? access?.value;
  clearTokens();
  const w = window as unknown as { google?: Gis };
  if (t && w.google) w.google.accounts.oauth2.revoke(t);
}
